import {
  createRuntimeReportingApi,
  type SubmitRuntimeAgentControllerReportsInput,
  toStructuredApiClientFailure,
} from "@wakeloop/api-client";
import type { AgentControllerInterfaceSnapshot } from "@wakeloop/schemas/types";
import { getAuthSessionState } from "../../config/auth-session";
import { readDaemonServiceContract } from "../../config/daemon-service-contract";
import { canAttachAuthTokenToRequest } from "../../config/request-headers";
import type { WakeLoopProfile } from "../../config/wakeloop-profiles-api";
import { createCliApiClientFoundation } from "../../space/api-client-foundation";
import type { LocalParticipationState } from "../../system/action-participation-planner";
import { resolveAgentProfilePrimaryAgentControllerReport } from "../../system/agent-profile-primary-binding";

export type RuntimeAgentControllerReportSubmission = {
  source: "runtime_reporting_api";
  status: "failed" | "submitted" | "unavailable";
  agentControllerRef?: string;
  agentControllerReportStatus?: "accepted" | "partial" | "rejected";
  deviceId?: string;
  lane?: string;
  reason?:
    | "auth_gateway_mismatch"
    | "auth_unavailable"
    | "agent_controller_ref_unavailable"
    | "registration_rejected"
    | "report_rejected"
    | "service_contract_unavailable"
    | "write_failed";
  reasonCodes: string[];
  authBaseUrl?: string;
  registrationStatus?: "created" | "reused";
  gatewayUrl?: string;
  guidanceReason?: "mismatch";
  wakeLoopServiceRuntimeId?: string;
};

export type RuntimeProviderConversationProofSubmission = {
  source: "runtime_reporting_api";
  status: "failed" | "submitted" | "unavailable";
  agentProfileId?: string;
  bindingId?: string;
  providerConversationProofStatus?: "accepted" | "partial" | "rejected";
  deviceId?: string;
  lane?: string;
  reason?:
    | "auth_gateway_mismatch"
    | "auth_unavailable"
    | "proof_rejected"
    | "registration_rejected"
    | "service_contract_unavailable"
    | "write_failed";
  reasonCodes: string[];
  authBaseUrl?: string;
  registrationStatus?: "created" | "reused";
  gatewayUrl?: string;
  guidanceReason?: "mismatch";
  wakeLoopServiceRuntimeId?: string;
};

export type RuntimeAgentControllerReportInput = {
  agentControllerRef: string;
  agentControllerInterface?: AgentControllerInterfaceSnapshot;
  diagnosticProfileId?: string;
  enabled?: boolean;
  localParticipationState: LocalParticipationState;
  reasonCodes: readonly string[];
};

export type RuntimeProviderConversationProofInput = {
  agentProfileId: string;
  bindingId: string;
  agentControllerRef: string;
  agentControllerConversationId: string;
  transportMode: "codex-app-server";
  provider: "codex";
  method: "codex_app_server_thread_resume";
  intrusive: false;
  observedAtMs: number;
  expiresAtMs: number;
  status: "failed" | "passed";
  errorCode?: string;
  errorMessage?: string;
  errorType?: string;
};

const MIN_REPORT_TTL_MS = 10 * 60_000;

export async function submitProfileRuntimeAgentControllerReport(input: {
  localParticipationState: LocalParticipationState;
  profile: WakeLoopProfile;
  reasonCodes: readonly string[];
}): Promise<RuntimeAgentControllerReportSubmission> {
  const agentControllerRef = await resolveProfileAgentControllerRef(
    input.profile
  );
  if (!agentControllerRef) {
    return unavailableSubmission({
      reason: "agent_controller_ref_unavailable",
      reasonCode: "runtime.reporting.agent_controller_ref_unavailable",
    });
  }

  return await submitRuntimeAgentControllerReport({
    agentControllerRef,
    diagnosticProfileId: input.profile.wakeLoopProfileId,
    localParticipationState: input.localParticipationState,
    reasonCodes: input.reasonCodes,
  });
}

export async function submitRuntimeAgentControllerReport(input: {
  agentControllerRef: string;
  agentControllerInterface?: AgentControllerInterfaceSnapshot;
  diagnosticProfileId?: string;
  localParticipationState: LocalParticipationState;
  reasonCodes: readonly string[];
}): Promise<RuntimeAgentControllerReportSubmission> {
  const [result] = await submitRuntimeAgentControllerReports({
    reports: [input],
  });
  if (!result) {
    throw new Error(
      "WakeLoop runtime reporting did not return a submission result."
    );
  }
  return result;
}

export async function submitRuntimeAgentControllerReports(input: {
  reports: readonly RuntimeAgentControllerReportInput[];
}): Promise<RuntimeAgentControllerReportSubmission[]> {
  // WAKELOOP_SERVICE_THIN_CORE: Runtime Agent Controller reporting evidence.
  // This reports local controller availability for the current local service
  // identity; it does not own Profile, Binding, Space, Wake, or final outcome
  // truth.
  if (input.reports.length === 0) {
    return [];
  }

  const serviceContract = await readDaemonServiceContract().catch(() => null);
  if (
    !(
      serviceContract?.deviceId &&
      serviceContract.gatewayUrl &&
      serviceContract.lane &&
      isFinitePositiveNumber(serviceContract.heartbeatMs)
    )
  ) {
    return unavailableSubmissions({
      reports: input.reports,
      reason: "service_contract_unavailable",
      reasonCode: "runtime.reporting.service_contract_unavailable",
    });
  }

  const authCompatibility = await resolveRuntimeReportingAuthCompatibility(
    serviceContract.gatewayUrl
  );
  if (authCompatibility.status === "unavailable") {
    return failedSubmissions({
      gatewayUrl: serviceContract.gatewayUrl,
      reports: input.reports,
      reason: "auth_unavailable",
      reasonCode: `runtime.reporting.auth_${authCompatibility.authState}`,
    });
  }
  if (authCompatibility.status === "mismatch") {
    return failedSubmissions({
      authBaseUrl: authCompatibility.authBaseUrl,
      gatewayUrl: serviceContract.gatewayUrl,
      guidanceReason: "mismatch",
      reports: input.reports,
      reason: "auth_gateway_mismatch",
      reasonCode: "runtime.reporting.auth_gateway_mismatch",
    });
  }

  try {
    const api = createRuntimeReportingApi(
      createCliApiClientFoundation(serviceContract.gatewayUrl)
    );
    const registration = await api.submitRegistration({
      deviceId: serviceContract.deviceId,
      lane: serviceContract.lane,
    });
    if (
      !(
        registration.status !== "rejected" &&
        registration.wakeLoopServiceRuntimeId
      )
    ) {
      return failedSubmissions({
        reports: input.reports,
        reason: "registration_rejected",
        reasonCode: `runtime.registration.${registration.reason ?? "rejected"}`,
      });
    }

    const observedAtMs = Date.now();
    const reportInput = buildRuntimeAgentControllerReportsInput({
      wakeLoopServiceRuntimeId: registration.wakeLoopServiceRuntimeId,
      deviceId: serviceContract.deviceId,
      heartbeatMs: serviceContract.heartbeatMs,
      includeAgentControllerInterface: true,
      lane: serviceContract.lane,
      observedAtMs,
      reports: input.reports,
    });
    const report = await submitAgentControllerReportsWithCompatibilityRetry({
      api,
      initialInput: reportInput,
      requestedReports: input.reports,
    });

    return resolveAgentControllerReportSubmissionResults({
      deviceId: serviceContract.deviceId,
      lane: serviceContract.lane,
      registrationStatus: registration.status,
      reports: input.reports,
      wakeLoopServiceRuntimeId: registration.wakeLoopServiceRuntimeId.value,
      submission: report,
    });
  } catch (error) {
    console.warn("[wakeloop.runtime_reporting.write_failed]", {
      error: toErrorMessage(error),
      event: "cli.runtime_reporting.write_failed",
      profileIds: input.reports
        .map((report) => report.diagnosticProfileId)
        .filter((profileId): profileId is string => Boolean(profileId)),
      reportCount: input.reports.length,
    });
    return failedSubmissions({
      reports: input.reports,
      reason: "write_failed",
      reasonCode: "runtime.reporting.write_failed",
    });
  }
}

export async function submitRuntimeProviderConversationProofs(input: {
  proofs: readonly RuntimeProviderConversationProofInput[];
}): Promise<RuntimeProviderConversationProofSubmission[]> {
  if (input.proofs.length === 0) {
    return [];
  }

  const serviceContract = await readDaemonServiceContract().catch(() => null);
  if (
    !(
      serviceContract?.deviceId &&
      serviceContract.gatewayUrl &&
      serviceContract.lane &&
      isFinitePositiveNumber(serviceContract.heartbeatMs)
    )
  ) {
    return unavailableProviderConversationProofSubmissions({
      proofs: input.proofs,
      reason: "service_contract_unavailable",
      reasonCode:
        "provider_conversation.runtime_reporting.service_contract_unavailable",
    });
  }

  const authCompatibility = await resolveRuntimeReportingAuthCompatibility(
    serviceContract.gatewayUrl
  );
  if (authCompatibility.status === "unavailable") {
    return failedProviderConversationProofSubmissions({
      gatewayUrl: serviceContract.gatewayUrl,
      proofs: input.proofs,
      reason: "auth_unavailable",
      reasonCode: `provider_conversation.runtime_reporting.auth_${authCompatibility.authState}`,
    });
  }
  if (authCompatibility.status === "mismatch") {
    return failedProviderConversationProofSubmissions({
      authBaseUrl: authCompatibility.authBaseUrl,
      gatewayUrl: serviceContract.gatewayUrl,
      guidanceReason: "mismatch",
      proofs: input.proofs,
      reason: "auth_gateway_mismatch",
      reasonCode:
        "provider_conversation.runtime_reporting.auth_gateway_mismatch",
    });
  }

  try {
    const api = createRuntimeReportingApi(
      createCliApiClientFoundation(serviceContract.gatewayUrl)
    );
    const registration = await api.submitRegistration({
      deviceId: serviceContract.deviceId,
      lane: serviceContract.lane,
    });
    if (
      !(
        registration.status !== "rejected" &&
        registration.wakeLoopServiceRuntimeId
      )
    ) {
      return failedProviderConversationProofSubmissions({
        proofs: input.proofs,
        reason: "registration_rejected",
        reasonCode: `provider_conversation.runtime_registration.${
          registration.reason ?? "rejected"
        }`,
      });
    }

    const submission = await api.submitProviderConversationProofs({
      deviceId: serviceContract.deviceId,
      lane: serviceContract.lane,
      proofs: input.proofs.map((proof) => ({
        ...proof,
      })),
      wakeLoopServiceRuntimeId: registration.wakeLoopServiceRuntimeId,
    });

    return resolveProviderConversationProofSubmissionResults({
      deviceId: serviceContract.deviceId,
      lane: serviceContract.lane,
      registrationStatus: registration.status,
      proofs: input.proofs,
      wakeLoopServiceRuntimeId: registration.wakeLoopServiceRuntimeId.value,
      submission,
    });
  } catch (error) {
    console.warn("[wakeloop.provider_conversation_proof.write_failed]", {
      error: toErrorMessage(error),
      event: "cli.provider_conversation_proof.write_failed",
      profileIds: input.proofs.map((proof) => proof.agentProfileId),
      proofCount: input.proofs.length,
    });
    return failedProviderConversationProofSubmissions({
      proofs: input.proofs,
      reason: "write_failed",
      reasonCode: "provider_conversation.runtime_reporting.write_failed",
    });
  }
}

function isFinitePositiveNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

async function resolveProfileAgentControllerRef(
  profile: WakeLoopProfile
): Promise<string | null> {
  const capability = await resolveAgentProfilePrimaryAgentControllerReport({
    profile,
  }).catch(() => null);
  return capability?.agentControllerRef ?? null;
}

function resolveReportStatus(input: {
  enabled?: boolean;
  localParticipationState: LocalParticipationState;
}): "disabled" | "ready" | "unavailable" | "unknown" {
  if (input.enabled === false) {
    return "disabled";
  }
  if (input.localParticipationState === "ready") {
    return "ready";
  }
  if (input.localParticipationState === "membership_only") {
    return "unknown";
  }
  return "unavailable";
}

function resolveReportReasonCodes(input: {
  enabled?: boolean;
  localParticipationState: LocalParticipationState;
  reasonCodes: readonly string[];
}): string[] {
  if (input.reasonCodes.length > 0) {
    return Array.from(new Set(input.reasonCodes));
  }
  return [
    `runtime.agent_controller_report.${resolveReportStatus({
      enabled: input.enabled,
      localParticipationState: input.localParticipationState,
    })}`,
  ];
}

function buildRuntimeAgentControllerReportsInput(input: {
  wakeLoopServiceRuntimeId: SubmitRuntimeAgentControllerReportsInput["wakeLoopServiceRuntimeId"];
  deviceId: string;
  heartbeatMs: number;
  includeAgentControllerInterface: boolean;
  lane: string;
  observedAtMs: number;
  reports: readonly RuntimeAgentControllerReportInput[];
}): SubmitRuntimeAgentControllerReportsInput {
  return {
    deviceId: input.deviceId,
    lane: input.lane,
    reports: input.reports.map((runtimeReport) => ({
      agentControllerRef: runtimeReport.agentControllerRef,
      ...(input.includeAgentControllerInterface &&
      runtimeReport.agentControllerInterface
        ? { agentControllerInterface: runtimeReport.agentControllerInterface }
        : {}),
      expiresAtMs:
        input.observedAtMs + Math.max(input.heartbeatMs * 3, MIN_REPORT_TTL_MS),
      observedAtMs: input.observedAtMs,
      reasonCodes: resolveReportReasonCodes({
        enabled: runtimeReport.enabled,
        localParticipationState: runtimeReport.localParticipationState,
        reasonCodes: runtimeReport.reasonCodes,
      }),
      status: resolveReportStatus({
        enabled: runtimeReport.enabled,
        localParticipationState: runtimeReport.localParticipationState,
      }),
    })),
    wakeLoopServiceRuntimeId: input.wakeLoopServiceRuntimeId,
  };
}

async function submitAgentControllerReportsWithCompatibilityRetry(input: {
  api: ReturnType<typeof createRuntimeReportingApi>;
  initialInput: SubmitRuntimeAgentControllerReportsInput;
  requestedReports: readonly RuntimeAgentControllerReportInput[];
}): Promise<RuntimeAgentControllerReportsSubmission> {
  try {
    return await input.api.submitAgentControllerReports(input.initialInput);
  } catch (error) {
    if (
      !shouldRetryWithoutAgentControllerInterface({
        error,
        requestedReports: input.requestedReports,
      })
    ) {
      throw error;
    }

    console.warn(
      "[wakeloop.runtime_reporting.agent_controller_interface_downgraded]",
      {
        error: toErrorMessage(error),
        event: "cli.runtime_reporting.agent_controller_interface_downgraded",
        profileIds: input.requestedReports
          .map((report) => report.diagnosticProfileId)
          .filter((profileId): profileId is string => Boolean(profileId)),
        reportCount: input.requestedReports.length,
      }
    );

    return await input.api.submitAgentControllerReports({
      ...input.initialInput,
      reports: input.initialInput.reports.map(
        ({ agentControllerInterface: _agentControllerInterface, ...report }) =>
          report
      ),
    });
  }
}

function shouldRetryWithoutAgentControllerInterface(input: {
  error: unknown;
  requestedReports: readonly RuntimeAgentControllerReportInput[];
}): boolean {
  if (
    !input.requestedReports.some((report) => report.agentControllerInterface)
  ) {
    return false;
  }

  const failure = toStructuredApiClientFailure(input.error);
  if (failure.status !== 400) {
    return false;
  }

  const diagnosticText = [
    failure.message,
    failure.schemaIssueMessage,
    failure.schemaIssuePath,
    failure.upstreamCode,
  ]
    .filter((value): value is string => Boolean(value))
    .join(" ")
    .toLowerCase();

  return diagnosticText.includes("agentcontrollerinterface");
}

type RuntimeAgentControllerReportsSubmission = {
  reason?: string;
  reports: readonly {
    agentControllerRef: string;
    reason?: string;
    status: "accepted" | "rejected";
  }[];
  status: "accepted" | "partial" | "rejected";
};

function resolveAgentControllerReportSubmissionResults(input: {
  deviceId: string;
  lane: string;
  registrationStatus: "created" | "reused";
  reports: readonly RuntimeAgentControllerReportInput[];
  wakeLoopServiceRuntimeId: string;
  submission: RuntimeAgentControllerReportsSubmission;
}): RuntimeAgentControllerReportSubmission[] {
  const submittedReportByAgentControllerRef = new Map(
    input.submission.reports.map((report) => [
      report.agentControllerRef,
      report,
    ])
  );

  return input.reports.map((requestedReport) => {
    const submittedReport = submittedReportByAgentControllerRef.get(
      requestedReport.agentControllerRef
    );
    if (
      input.submission.status === "rejected" ||
      !submittedReport ||
      submittedReport.status === "rejected"
    ) {
      return failedSubmission({
        agentControllerRef: requestedReport.agentControllerRef,
        reason: "report_rejected",
        reasonCode: resolveRejectedReportReasonCode({
          report: submittedReport,
          submission: input.submission,
        }),
        wakeLoopServiceRuntimeId: input.wakeLoopServiceRuntimeId,
      });
    }

    return {
      agentControllerRef: requestedReport.agentControllerRef,
      agentControllerReportStatus: submittedReport.status,
      deviceId: input.deviceId,
      lane: input.lane,
      reasonCodes: [],
      registrationStatus: input.registrationStatus,
      wakeLoopServiceRuntimeId: input.wakeLoopServiceRuntimeId,
      source: "runtime_reporting_api",
      status: "submitted",
    };
  });
}

function resolveRejectedReportReasonCode(input: {
  report:
    | {
        reason?: string;
        status: "accepted" | "rejected";
      }
    | undefined;
  submission: {
    reason?: string;
    status: "accepted" | "partial" | "rejected";
  };
}): string {
  if (!input.report) {
    return `runtime.agent_controller_report.${
      input.submission.reason ?? "result_missing"
    }`;
  }
  return `runtime.agent_controller_report.${
    input.report.reason ?? input.submission.reason ?? "rejected"
  }`;
}

type RuntimeProviderConversationProofsSubmission = {
  reason?: string;
  proofs: readonly {
    agentProfileId: string;
    bindingId: string;
    reason?: string;
    status: "accepted" | "rejected";
  }[];
  status: "accepted" | "partial" | "rejected";
};

function resolveProviderConversationProofSubmissionResults(input: {
  deviceId: string;
  lane: string;
  registrationStatus: "created" | "reused";
  proofs: readonly RuntimeProviderConversationProofInput[];
  wakeLoopServiceRuntimeId: string;
  submission: RuntimeProviderConversationProofsSubmission;
}): RuntimeProviderConversationProofSubmission[] {
  const submittedProofByIdentity = new Map(
    input.submission.proofs.map((proof) => [
      buildProviderConversationProofSubmissionKey(proof),
      proof,
    ])
  );

  return input.proofs.map((requestedProof) => {
    const submittedProof = submittedProofByIdentity.get(
      buildProviderConversationProofSubmissionKey(requestedProof)
    );
    if (
      input.submission.status === "rejected" ||
      !submittedProof ||
      submittedProof.status === "rejected"
    ) {
      return failedProviderConversationProofSubmission({
        agentProfileId: requestedProof.agentProfileId,
        bindingId: requestedProof.bindingId,
        reason: "proof_rejected",
        reasonCode: resolveRejectedProviderConversationProofReasonCode({
          proof: submittedProof,
          submission: input.submission,
        }),
        wakeLoopServiceRuntimeId: input.wakeLoopServiceRuntimeId,
      });
    }

    return {
      agentProfileId: requestedProof.agentProfileId,
      bindingId: requestedProof.bindingId,
      providerConversationProofStatus: submittedProof.status,
      deviceId: input.deviceId,
      lane: input.lane,
      reasonCodes: [],
      registrationStatus: input.registrationStatus,
      wakeLoopServiceRuntimeId: input.wakeLoopServiceRuntimeId,
      source: "runtime_reporting_api",
      status: "submitted",
    };
  });
}

function resolveRejectedProviderConversationProofReasonCode(input: {
  proof:
    | {
        reason?: string;
        status: "accepted" | "rejected";
      }
    | undefined;
  submission: {
    reason?: string;
    status: "accepted" | "partial" | "rejected";
  };
}): string {
  if (!input.proof) {
    return `provider_conversation.proof.${
      input.submission.reason ?? "result_missing"
    }`;
  }
  return `provider_conversation.proof.${
    input.proof.reason ?? input.submission.reason ?? "rejected"
  }`;
}

function buildProviderConversationProofSubmissionKey(input: {
  agentProfileId: string;
  bindingId: string;
}): string {
  return `${input.agentProfileId}:${input.bindingId}`;
}

function unavailableSubmissions(input: {
  reports: readonly RuntimeAgentControllerReportInput[];
  reason: Extract<
    RuntimeAgentControllerReportSubmission["reason"],
    "service_contract_unavailable"
  >;
  reasonCode: string;
}): RuntimeAgentControllerReportSubmission[] {
  return input.reports.map((report) =>
    unavailableSubmission({
      agentControllerRef: report.agentControllerRef,
      reason: input.reason,
      reasonCode: input.reasonCode,
    })
  );
}

function failedSubmissions(input: {
  authBaseUrl?: string;
  gatewayUrl?: string;
  guidanceReason?: "mismatch";
  reports: readonly RuntimeAgentControllerReportInput[];
  reason: Extract<
    RuntimeAgentControllerReportSubmission["reason"],
    | "auth_gateway_mismatch"
    | "auth_unavailable"
    | "registration_rejected"
    | "report_rejected"
    | "write_failed"
  >;
  reasonCode: string;
  wakeLoopServiceRuntimeId?: string;
}): RuntimeAgentControllerReportSubmission[] {
  return input.reports.map((report) =>
    failedSubmission({
      agentControllerRef: report.agentControllerRef,
      ...(input.authBaseUrl ? { authBaseUrl: input.authBaseUrl } : {}),
      ...(input.gatewayUrl ? { gatewayUrl: input.gatewayUrl } : {}),
      ...(input.guidanceReason ? { guidanceReason: input.guidanceReason } : {}),
      reason: input.reason,
      reasonCode: input.reasonCode,
      ...(input.wakeLoopServiceRuntimeId
        ? { wakeLoopServiceRuntimeId: input.wakeLoopServiceRuntimeId }
        : {}),
    })
  );
}

function unavailableProviderConversationProofSubmissions(input: {
  proofs: readonly RuntimeProviderConversationProofInput[];
  reason: Extract<
    RuntimeProviderConversationProofSubmission["reason"],
    "service_contract_unavailable"
  >;
  reasonCode: string;
}): RuntimeProviderConversationProofSubmission[] {
  return input.proofs.map((proof) =>
    unavailableProviderConversationProofSubmission({
      agentProfileId: proof.agentProfileId,
      bindingId: proof.bindingId,
      reason: input.reason,
      reasonCode: input.reasonCode,
    })
  );
}

function failedProviderConversationProofSubmissions(input: {
  authBaseUrl?: string;
  gatewayUrl?: string;
  guidanceReason?: "mismatch";
  proofs: readonly RuntimeProviderConversationProofInput[];
  reason: Extract<
    RuntimeProviderConversationProofSubmission["reason"],
    | "auth_gateway_mismatch"
    | "auth_unavailable"
    | "proof_rejected"
    | "registration_rejected"
    | "write_failed"
  >;
  reasonCode: string;
  wakeLoopServiceRuntimeId?: string;
}): RuntimeProviderConversationProofSubmission[] {
  return input.proofs.map((proof) =>
    failedProviderConversationProofSubmission({
      agentProfileId: proof.agentProfileId,
      bindingId: proof.bindingId,
      ...(input.authBaseUrl ? { authBaseUrl: input.authBaseUrl } : {}),
      ...(input.gatewayUrl ? { gatewayUrl: input.gatewayUrl } : {}),
      ...(input.guidanceReason ? { guidanceReason: input.guidanceReason } : {}),
      reason: input.reason,
      reasonCode: input.reasonCode,
      ...(input.wakeLoopServiceRuntimeId
        ? { wakeLoopServiceRuntimeId: input.wakeLoopServiceRuntimeId }
        : {}),
    })
  );
}

async function resolveRuntimeReportingAuthCompatibility(
  gatewayUrl: string
): Promise<
  | {
      status: "compatible";
    }
  | {
      status: "mismatch";
      authBaseUrl: string;
    }
  | {
      status: "unavailable";
      authState: "error" | "expired" | "invalid" | "missing";
    }
> {
  const authState = await getAuthSessionState().catch(() => null);
  if (!authState) {
    return { authState: "error", status: "unavailable" };
  }
  if (!(authState.state === "valid" && authState.session)) {
    // A request sent without credentials is a guaranteed gateway 401 that
    // surfaces as a bare "authentication required" with no cause. Refuse to
    // submit instead, so the failure carries the local auth state.
    return {
      authState:
        authState.state === "expired" || authState.state === "invalid"
          ? authState.state
          : "missing",
      status: "unavailable",
    };
  }
  if (canAttachAuthTokenToRequest(authState.session, gatewayUrl)) {
    return { status: "compatible" };
  }
  return {
    authBaseUrl: authState.session.authBaseUrl,
    status: "mismatch",
  };
}

function unavailableSubmission(input: {
  agentControllerRef?: string;
  reason: Extract<
    RuntimeAgentControllerReportSubmission["reason"],
    "agent_controller_ref_unavailable" | "service_contract_unavailable"
  >;
  reasonCode: string;
}): RuntimeAgentControllerReportSubmission {
  return {
    ...(input.agentControllerRef
      ? { agentControllerRef: input.agentControllerRef }
      : {}),
    reason: input.reason,
    reasonCodes: [input.reasonCode],
    source: "runtime_reporting_api",
    status: "unavailable",
  };
}

function failedSubmission(input: {
  authBaseUrl?: string;
  agentControllerRef?: string;
  gatewayUrl?: string;
  guidanceReason?: "mismatch";
  reason: Extract<
    RuntimeAgentControllerReportSubmission["reason"],
    | "auth_gateway_mismatch"
    | "auth_unavailable"
    | "registration_rejected"
    | "report_rejected"
    | "write_failed"
  >;
  reasonCode: string;
  wakeLoopServiceRuntimeId?: string;
}): RuntimeAgentControllerReportSubmission {
  return {
    ...(input.authBaseUrl ? { authBaseUrl: input.authBaseUrl } : {}),
    ...(input.agentControllerRef
      ? { agentControllerRef: input.agentControllerRef }
      : {}),
    ...(input.gatewayUrl ? { gatewayUrl: input.gatewayUrl } : {}),
    ...(input.guidanceReason ? { guidanceReason: input.guidanceReason } : {}),
    ...(input.wakeLoopServiceRuntimeId
      ? { wakeLoopServiceRuntimeId: input.wakeLoopServiceRuntimeId }
      : {}),
    reason: input.reason,
    reasonCodes: [input.reasonCode],
    source: "runtime_reporting_api",
    status: "failed",
  };
}

function unavailableProviderConversationProofSubmission(input: {
  agentProfileId: string;
  bindingId: string;
  reason: Extract<
    RuntimeProviderConversationProofSubmission["reason"],
    "service_contract_unavailable"
  >;
  reasonCode: string;
}): RuntimeProviderConversationProofSubmission {
  return {
    agentProfileId: input.agentProfileId,
    bindingId: input.bindingId,
    reason: input.reason,
    reasonCodes: [input.reasonCode],
    source: "runtime_reporting_api",
    status: "unavailable",
  };
}

function failedProviderConversationProofSubmission(input: {
  authBaseUrl?: string;
  agentProfileId: string;
  bindingId: string;
  gatewayUrl?: string;
  guidanceReason?: "mismatch";
  reason: Extract<
    RuntimeProviderConversationProofSubmission["reason"],
    | "auth_gateway_mismatch"
    | "auth_unavailable"
    | "proof_rejected"
    | "registration_rejected"
    | "write_failed"
  >;
  reasonCode: string;
  wakeLoopServiceRuntimeId?: string;
}): RuntimeProviderConversationProofSubmission {
  return {
    ...(input.authBaseUrl ? { authBaseUrl: input.authBaseUrl } : {}),
    agentProfileId: input.agentProfileId,
    bindingId: input.bindingId,
    ...(input.gatewayUrl ? { gatewayUrl: input.gatewayUrl } : {}),
    ...(input.guidanceReason ? { guidanceReason: input.guidanceReason } : {}),
    ...(input.wakeLoopServiceRuntimeId
      ? { wakeLoopServiceRuntimeId: input.wakeLoopServiceRuntimeId }
      : {}),
    reason: input.reason,
    reasonCodes: [input.reasonCode],
    source: "runtime_reporting_api",
    status: "failed",
  };
}

function toErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    const message = error.message.trim();
    return message.length > 0 ? message : "unknown error";
  }
  const message = String(error ?? "").trim();
  return message.length > 0 ? message : "unknown error";
}
